/**
 * PhysiologyEngine — motor de respuesta farmacodinámica en tiempo real.
 *
 * Mantiene una lista de drogas administradas (con timestamp) y, dado un
 * instante t actual, calcula el delta acumulado sobre cada vital.
 *
 * Modelo por droga-efecto:
 *   - Rampa lineal de 0 a peak entre t0 y t0+onset
 *   - Plateau al peak hasta t0+onset + (duration * 0.4)
 *   - Decay lineal al 0 entre ese punto y t0+duration
 *
 * Es una aproximación educativa; no pretende fidelidad farmacocinética real.
 * Lo importante es que el alumno vea cambios progresivos (no instantáneos)
 * cuando administra una droga, lo que se siente mucho más real que un delta
 * súbito aplicado de una sola vez.
 */

import { DRUG_CATALOG, findDrug, type DrugEffect, type Vital } from './DrugCatalog';

export interface DrugAdministration {
  id: string;                 // droga del catálogo
  startAt: number;            // Date.now() al administrar
  /** Multiplicador de dosis (1 = estándar, 0.5 = media dosis). */
  doseMultiplier: number;
}

export class PhysiologyEngine {
  private administrations: DrugAdministration[] = [];
  private listeners: Array<(admin: DrugAdministration) => void> = [];

  administer(id: string, doseMultiplier = 1): DrugAdministration | null {
    const def = findDrug(id);
    if (!def) {
      console.warn('[PhysiologyEngine] droga desconocida:', id);
      return null;
    }
    const admin: DrugAdministration = { id, startAt: Date.now(), doseMultiplier };
    this.administrations.push(admin);
    this.listeners.forEach(l => { try { l(admin); } catch { /* noop */ } });
    // Limpieza: descartar administraciones terminadas (más allá de su duración máxima)
    this.pruneExpired();
    return admin;
  }

  onAdminister(cb: (admin: DrugAdministration) => void): () => void {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter(l => l !== cb); };
  }

  /**
   * Calcula el delta acumulado sobre cada vital en el instante `now`.
   *
   * Modelo de saturación: dosis repetidas de la MISMA droga tienen retorno
   * decreciente (cada dosis adicional aporta el 60% de la anterior). Esto
   * evita que 3 ampollas de adrenalina sumen +90 lineal — refleja down-
   * regulation de receptores y el manejo clínico real.
   *
   * Drogas DISTINTAS sobre el mismo vital se suman sin penalización (no
   * modelamos interacciones farmacológicas; sería mucho más complejo).
   */
  getDeltas(now: number = Date.now()): Partial<Record<Vital, number>> {
    const deltas: Partial<Record<Vital, number>> = {};
    // Agrupar administraciones por droga para aplicar diminishing returns
    const byDrug = new Map<string, DrugAdministration[]>();
    for (const admin of this.administrations) {
      const arr = byDrug.get(admin.id) ?? [];
      arr.push(admin);
      byDrug.set(admin.id, arr);
    }

    for (const [drugId, admins] of byDrug.entries()) {
      const def = findDrug(drugId);
      if (!def) continue;
      // Ordenar por momento ascendente (la 1ª dosis = factor 1, 2ª = 0.6, 3ª = 0.36, …)
      admins.sort((a, b) => a.startAt - b.startAt);
      admins.forEach((admin, idx) => {
        const elapsedSec = (now - admin.startAt) / 1000;
        const dimFactor = Math.pow(0.6, idx); // diminishing returns por dosis adicional
        for (const eff of def.effects) {
          const contribution = this.effectAt(eff, elapsedSec) * admin.doseMultiplier * dimFactor;
          deltas[eff.vital] = (deltas[eff.vital] ?? 0) + contribution;
        }
      });
    }

    // Cap por vital: ningún vital puede ser modificado >2.5x el peak más
    // grande del catálogo (ej. HR no sube más de +75 por farmacología).
    // Es un safety net para evitar valores absurdos por bugs de stack.
    const VITAL_CAPS: Record<Vital, number> = {
      hr: 75, sbp: 90, dbp: 40, spo2: 18, rr: 12, temp: 3,
    };
    for (const k of Object.keys(deltas) as Vital[]) {
      const v = deltas[k]!;
      const cap = VITAL_CAPS[k];
      if (v > cap) deltas[k] = cap;
      else if (v < -cap) deltas[k] = -cap;
    }
    return deltas;
  }

  /** Lista drogas actualmente activas (no expiradas). */
  activeDrugs(now: number = Date.now()): DrugAdministration[] {
    return this.administrations.filter(a => {
      const def = findDrug(a.id);
      if (!def) return false;
      const maxDur = Math.max(0, ...def.effects.map(e => e.durationSec));
      return (now - a.startAt) / 1000 < maxDur;
    });
  }

  reset(): void {
    this.administrations = [];
  }

  /**
   * Devuelve la curva farmacodinámica en `elapsedSec` para un efecto.
   * Rampa linear onset → plateau 40% de duration → decay linear.
   */
  private effectAt(eff: DrugEffect, elapsedSec: number): number {
    if (elapsedSec < 0 || elapsedSec > eff.durationSec) return 0;
    const peak = eff.delta;
    const onset = Math.max(1, eff.onsetSec);
    const plateauEnd = onset + eff.durationSec * 0.4;
    if (elapsedSec <= onset) {
      return peak * (elapsedSec / onset);
    }
    if (elapsedSec <= plateauEnd) {
      return peak;
    }
    // Decay desde plateauEnd hasta durationSec
    const decaySpan = eff.durationSec - plateauEnd;
    if (decaySpan <= 0) return 0;
    const t = (elapsedSec - plateauEnd) / decaySpan;
    return peak * (1 - Math.max(0, Math.min(1, t)));
  }

  private pruneExpired(): void {
    const now = Date.now();
    this.administrations = this.administrations.filter(a => {
      const def = findDrug(a.id);
      if (!def) return false;
      const maxDur = Math.max(60, ...def.effects.map(e => e.durationSec));
      return (now - a.startAt) / 1000 < maxDur;
    });
  }
}

export const physiologyEngine = new PhysiologyEngine();

/** Reexportar el catálogo para que consumidores tengan un solo entrypoint. */
export { DRUG_CATALOG };
